import { Component, Inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PortalMaterialModule } from '../../material.components.module';
import { DragDropDirective } from '../../common/directives/drag-drop/drag-drop.directive';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { TranslationService } from '../../common/services/languages/translation.service';
import * as XLSX from 'xlsx';
import { FileDispatcher } from '../../store/file/file-dispatcher';
import { throwError } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { NgRedux } from '@angular-redux/store';
import { AppState } from '../../store/state.model';
import { DialogBoxComponent } from '../../common/components/dialog-box/dialog-box.component';
import { ASIControl } from './presenter/asi.presenter';
import { PACKAGEControl } from './presenter/package.presenter';
import { CommonDispatcher } from '../../store/common/common-dispatcher';
import { GoodWillControl } from './presenter/goodwill.presenter';
type AOA = Array<Array<string | number>>;

@Component({
    selector: 'app-upload-dialog',
    templateUrl: './upload-dialog.component.html',
    styleUrls: ['./upload-dialog.component.scss'],
    imports: [CommonModule, TranslateModule, PortalMaterialModule, DragDropDirective]
})

export class UploadDialogComponent {
  /**
   * @arg upload_ready to store if upload is ready
   **/
  upload_ready: boolean;
  /**
   * @arg upload_start to store if upload is started
   **/
  upload_start: boolean;
  /**
   * @arg uploaded_file_name to store uploaded file name
   **/
  uploaded_file_name: string;
  /**
   * @arg uploaded_parsedfile to store the parsed uploaded capabilities or packages
   **/
  uploaded_parsedfile: AOA;
  /**
  * @arg type to store the type
  **/
  fileType: string;
  /**
   * @arg model stores the capabilites or packages state
  /**
  * @var uploadASIGenerations stores the asi upload generation
  **/
  public uploadASIGenerations: Array<string>;
  /**
  * @var selectedUploadASIGeneration stores the asi upload generation
  **/
  public selectedUploadASIGeneration: string;
    /**
   * @arg selectedAsiGenUploadReady to store if upload is ready
   **/
  public selectedAsiGenUploadReady: boolean;
  public model: any;
  constructor(
    public dialogRef: MatDialogRef<UploadDialogComponent>,
    public translate: TranslationService,
    public fileDispatcher: FileDispatcher,
    public router: Router,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    public dialog: MatDialog,
    public ngRedux: NgRedux<AppState>,
    public commonDispatcher: CommonDispatcher,
    public route: ActivatedRoute
  ) {
    this.initialize();
    this.fileType = data.type;
    this.uploadASIGenerations = ['24MM', '17CYPLUS', '21MM'];
    this.selectedAsiGenUploadReady = false;
   }
  /**
   * @method initialize initializes the variables
   **/
  private initialize() {
    this.uploaded_parsedfile = [[], []];
  }
  /**
  * @method close closes the dialog
  **/
  private close(): void {
    this.dialogRef.close({
      'asiUploadGenerationSelection': this.selectedUploadASIGeneration
    });
  }
  /**
   * @method onFileUpload on file upload action
   * @param evt parsed object from the file
   **/
  public onFileUpload(evt: any): void {
    const target: DataTransfer = <DataTransfer>(evt.target);
    this.parseExcel(target.files);
  }
  /**
   * @method clearFile clears the file
   **/
  public clearFile(): void {
    this.upload_ready = false;
    this.uploaded_file_name = '';
    this.fileDispatcher.setUploadedFiledata({});
  }
  /**
   * @method parseExcel parses the uploaded file
   * @param files array of files. But picks the first file only
   **/
  private parseExcel(files): void {
    if (files.length !== 1) {
      throw new Error('Cannot use multiple files');
    }
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const wb: XLSX.WorkBook = XLSX.read(e.target.result, { type: 'binary' });
      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];
      /* save data */
      this.uploaded_parsedfile = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      this.uploaded_file_name = files[0].name;
      this.upload_ready = true;
    };
    reader.readAsBinaryString(files[0]);
  }
  /**
   * @method upload upload button action
   **/
  public upload(): void {
    this.upload_start = true;
    switch (
    (this.uploaded_parsedfile.length !== 0 && this.uploaded_parsedfile[0].length !== 0) &&
    ((this.fileType === 'asi' && ASIControl.checkColumns(this.uploaded_parsedfile[0] as string[], this.selectedUploadASIGeneration)) ||
      (this.fileType === 'package' && PACKAGEControl.checkColumns(this.uploaded_parsedfile[0] as string[])) ||
      (this.fileType === 'goodwill' && GoodWillControl.checkColumns(this.uploaded_parsedfile[0] as string[])))
    ) {
      case true:
        this.validFileActions();
        break;
      default:
        this.openFailedDialog();
        break;
    }
  }
  /**
  * @method validFileActions actions after uploading a valid file
  **/
  validFileActions() {
    this.fileDispatcher.setUploadedFiledata(this.uploaded_parsedfile);
    this.fileDispatcher.setFilename(this.uploaded_file_name);
    this.fileDispatcher.setFiletype(this.fileType);
    this.fileDispatcher.setGeneration(this.selectedUploadASIGeneration);
    this.commonDispatcher.setStep('upload');
    this.commonDispatcher.showResults('true');
    this.close();
  }
  /**
  * @method cancel cancel button action
  **/
  public cancel(): void {
    this.fileDispatcher.setUploadedFiledata({});
    this.fileDispatcher.setFilename('');
    this.close();
  }
  /**
  * @method onFileDropped actions on file drop
  * @param fileList list of files
  **/
  public onFileDropped(fileList: FileList): void {
    if (!!fileList) {
      this.parseExcel(fileList);
    }
  }
  /**
  * @method onFileInvalid action on invalid file
  * @param fileList parsed object from the file
  **/
  public onFileInvalid(fileList: FileList): void {
    throwError(() => 'invalid file');
  }
  /**
  * @method openFailedDialog opens the dialog box for a failed upload
  **/
  public openFailedDialog(): void {
    this.router.navigate(['/dashboard/svpp'],
      { queryParams: {}, skipLocationChange: true });
    this.dialog.open(DialogBoxComponent, {
      data: {
        type: 'upload-failed'
      },
      width: '30em',
      height: 'auto'
    });
  }

/**
 * @method setSelectedAsiGenUploadReady to set selectedAsiGenUploadReady to true state
*/
  public setSelectedAsiGenUploadReady(): void {
    this.selectedAsiGenUploadReady = true;
  }

/**
 * @method isAsiFileType returns if passed FileType is of asi
*/
  public isAsiFileType() {
    return this.fileType === 'asi';
  }
}

